import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { DsToastService } from '@bmw-ds/components';
import { NGXLogger } from 'ngx-logger';

import { GcdmService } from '../../../consent/clients/gcdm/gcdm.service';
import { GcdmRequestModelMapper } from '../../../consent/clients/gcdm/mapper/request-model.mapper';
import { GcdmLanguageProvider } from '../../../consent/services/gcdm-language.provider';
import { Customer } from '../../../customer/model/customer.model';
import { IdCreationStatus } from '../../../customer/services/customer.service';
import { BrandedIdType } from '../../../customer-creation/model/branded-id-type';
import { useI18n } from '../../../i18n/services/i18n.service';
import { CreateBmwMiniIdComponent } from './create-bmw-mini-id.component';

@Component({
  selector: 'cdf-create-bmw-mini-id-container',
  providers: [GcdmLanguageProvider],
  imports: [CreateBmwMiniIdComponent],
  template: `
    <!-- emailForBmwMiniId should always be defined. If not, modal is closed and error is displayed for user anyway. -->
    @if (emailForBmwMiniId(); as emailForBmwMiniId) {
      <cdf-create-bmw-mini-id
        [email]="emailForBmwMiniId"
        [correspondenceLanguage]="customer()?.correspondenceLanguage"
        [(idCreationStatus)]="idCreationStatus"
        (createBmwId)="createBmwMiniId(BrandedIdType.BMW)"
        (createMiniId)="createBmwMiniId(BrandedIdType.MINI)"
        (closeModal)="closeModal.emit()"
      />
    }
  `,
  styleUrl: './create-bmw-mini-id.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateBmwMiniIdContainer {
  readonly gcdmService = inject(GcdmService);
  readonly gcdmRequestModelMapper = inject(GcdmRequestModelMapper);
  protected readonly gcdmLanguageProvider = inject(GcdmLanguageProvider);
  private readonly logger = inject(NGXLogger);
  private readonly dsToastService = inject(DsToastService);

  private readonly i18n = useI18n();

  readonly customer = input<Customer>();
  readonly successfullyInitializedIdCreation = output();
  readonly closeModal = output();

  private toastId = 'CREATE_BMW_MINI_ID_TOAST';

  readonly idCreationStatus = signal<IdCreationStatus | undefined>(undefined);

  readonly emailForBmwMiniId = computed(() => {
    const email = this.customer()?.privateContact.email ?? this.customer()?.businessContact.email;
    if (!email) {
      this.logger.error('#CDFTF0065 No private and no business email was found for creating a BMW/MINI ID.');
      this.showMissingEmailError();
      this.closeModal.emit();
    }
    return email;
  });

  async createBmwMiniId(type: BrandedIdType.MINI | BrandedIdType.BMW) {
    this.dsToastService.popToast(this.toastId);
    this.idCreationStatus.set(IdCreationStatus.LOADING);
    try {
      const gcid = this.customer()?.gcid;
      if (!gcid) {
        throw new Error(`#CDFTF0067 No GCID present while trying to create a ${type} ID.`);
      }
      const emailForId = this.emailForBmwMiniId();

      if (!emailForId) {
        throw new Error('#CDFTF0129 Not able to create BMW/MINI ID as no E-Mail is provided by user.');
      }

      await this.gcdmService.preregister(
        this.gcdmRequestModelMapper.mapToPreregistrationData(
          gcid,
          type,
          this.gcdmLanguageProvider.resolvedConsentLanguage(),
          emailForId,
        ),
      );
      this.idCreationStatus.set(IdCreationStatus.SUCCESSFUL);
      this.successfullyInitializedIdCreation.emit();
      this.showIdCreationSuccess(type);
      this.closeModal.emit();
    } catch (error) {
      this.logger.error(`#CDFTF0066 Failed to create ${type} ID`, error);
      this.idCreationStatus.set(IdCreationStatus.FAILED);
    }
  }

  private showIdCreationSuccess(type: BrandedIdType.MINI | BrandedIdType.BMW) {
    this.dsToastService.pushToast({
      toastTitle: this.i18n('createBmwOrMiniIdModal.successTitle', { type }),
      toastText: this.i18n('createBmwOrMiniIdModal.successMessage'),
      id: this.toastId,
      tone: 'positive',
    });
  }

  private showMissingEmailError() {
    this.dsToastService.pushToast({
      toastTitle: this.i18n('createBmwOrMiniIdModal.missingEmailErrorTitle'),
      toastText: this.i18n('createBmwOrMiniIdModal.missingEmailErrorMessage'),
      id: this.toastId,
      tone: 'critical',
    });
  }

  protected readonly BrandedIdType = BrandedIdType;
}
